import { createElement } from 'react';
import type { TreeData, TreeNodeType, TreeRowAction } from '@/shared/ui';
import { CalendarDays, FolderKanban, Layers3, Package2, Pencil, Plus } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import type { GetPlanningTreeResponse } from './plannerApi';

/**
 * TreeView velikost typových ikon nevynucuje (ikona si ji vlastní) —
 * Lucide glyfy proto obalíme na 12 px odpovídajících hustotě stromu.
 */
function sizedTreeIcon(icon: React.FC<{ className?: string }>): React.FC<{ className?: string }> {
  return function SizedTreeIcon({ className }: { className?: string }) {
    return createElement(icon, { className: cn('h-3 w-3', className) });
  };
}

const ProjectIcon = sizedTreeIcon(FolderKanban);
const ProductIcon = sizedTreeIcon(Package2);
const SerieIcon = sizedTreeIcon(Layers3);
const TermIcon = sizedTreeIcon(CalendarDays);

export type PlannerEntityType = 'project' | 'product' | 'serie' | 'term';

export const PLANNER_ENTITY_TYPES = [
  'project',
  'product',
  'serie',
  'term',
] as const satisfies readonly PlannerEntityType[];

export interface PlannerTreeActionHandlers {
  onEditProject: (projectId: string) => void;
  onAddProduct: (projectId: string) => void;
  onAddSerie: (productId: string) => void;
  onAddTerms: (serieId: string) => void;
}

export interface PlannerTreeLabels {
  project: string;
  product: string;
  serie: string;
  term: string;
  editProject: string;
  addProduct: string;
  addSerie: string;
  addTerms: string;
}

export function buildPlannerTreeData(
  response: GetPlanningTreeResponse,
  labels: PlannerTreeLabels,
  handlers: PlannerTreeActionHandlers
): TreeData {
  const nodesById: TreeData['nodesById'] = {};
  const rootIds: string[] = [];

  for (const project of response.projects) {
    rootIds.push(project.projectId);
    nodesById[project.projectId] = {
      id: project.projectId,
      label: project.name,
      type: 'project',
      parentId: null,
      childIds: project.products.map((product) => product.productId),
    };

    for (const product of project.products) {
      nodesById[product.productId] = {
        id: product.productId,
        label: product.name,
        type: 'product',
        parentId: project.projectId,
        childIds: product.series.map((serie) => serie.productSerieId),
      };

      for (const serie of product.series) {
        nodesById[serie.productSerieId] = {
          id: serie.productSerieId,
          label: serie.name,
          type: 'serie',
          parentId: product.productId,
          childIds: serie.terms.map((term) => term.termId),
        };

        for (const term of serie.terms) {
          nodesById[term.termId] = {
            id: term.termId,
            label: term.name,
            type: 'term',
            parentId: serie.productSerieId,
            childIds: [],
          };
        }
      }
    }
  }

  const typeDefinitions: TreeNodeType[] = [
    { id: 'project', label: labels.project, icon: ProjectIcon },
    { id: 'product', label: labels.product, icon: ProductIcon },
    { id: 'serie', label: labels.serie, icon: SerieIcon },
    { id: 'term', label: labels.term, icon: TermIcon },
  ];

  // FR-013: na řádku TermGroup není dostupná žádná plus akce.
  // Plus akce vznikají jen pro project (přidat produkt), product (přidat seriál) a serie (přidat termíny).
  // Projekt má navíc akci editace (otevře formulář projektu v editačním režimu).
  const actionsByType: TreeRowAction[] = [
    {
      id: 'edit-project',
      label: labels.editProject,
      icon: Pencil,
      appliesTo: ['project'],
      onClick: handlers.onEditProject,
    },
    {
      id: 'add-product',
      label: labels.addProduct,
      icon: Plus,
      appliesTo: ['project'],
      onClick: handlers.onAddProduct,
    },
    {
      id: 'add-serie',
      label: labels.addSerie,
      icon: Plus,
      appliesTo: ['product'],
      onClick: handlers.onAddSerie,
    },
    {
      id: 'add-terms',
      label: labels.addTerms,
      icon: Plus,
      appliesTo: ['serie'],
      onClick: handlers.onAddTerms,
    },
  ];

  return {
    nodesById,
    rootIds,
    typeDefinitions,
    actionsByType,
  };
}

/**
 * Vrátí seznam ID předků daného uzlu v pořadí od nejbližšího rodiče k rootu.
 * Použití: programatický `expandToNode` po refreshi stromu nebo po vytvoření nového uzlu.
 */
export function getAncestorIds(treeData: TreeData, nodeId: string): string[] {
  const ancestors: string[] = [];
  let current = treeData.nodesById[nodeId];
  while (current?.parentId) {
    ancestors.push(current.parentId);
    current = treeData.nodesById[current.parentId];
  }
  return ancestors;
}

/**
 * `expandToNode` — sjednotí množinu rozbalených uzlů s předky cílového uzlu,
 * aby zůstal viditelný i po programatickém setActive.
 */
export function expandToNode(
  treeData: TreeData,
  nodeId: string,
  baseExpanded: Iterable<string> = []
): Set<string> {
  const next = new Set(baseExpanded);
  for (const ancestorId of getAncestorIds(treeData, nodeId)) {
    next.add(ancestorId);
  }
  return next;
}

/**
 * `setActive` — vrátí trojici (active, expanded) pro programatickou aktivaci.
 * Volající ji propaguje do agenda state (activeNodeId) a do TreeView (defaultExpandedIds).
 */
export function setActive(
  treeData: TreeData,
  nodeId: string,
  baseExpanded: Iterable<string> = []
): { activeNodeId: string; expandedIds: Set<string> } {
  return {
    activeNodeId: nodeId,
    expandedIds: expandToNode(treeData, nodeId, baseExpanded),
  };
}
